import React, { useState, useCallback, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import useAPI from "hooks/useAPI.js";
import InputErrorMessage from "components/utils/InputErrorMessage.js";
import SchedulingService from "services/SchedulingService";
import DateUtils from "utils/DateUtils.js";
import PopUpModal from "components/utils/PopUpModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import CALENDAR_EVENT_CANCEL_TYPES from "utils/constants/CalendarEventCancelTypes";

const UpdateAppointmentModal = ({
  hospitalId,
  appointmentId,
  calendarEventId,
  onCloseHandler,
}) => {
  const [eventGuestList, setEventGuestList] = useState([]);
  const [eventGuest, setEventGuest] = useState("");
  const [isEventRecurring, setIsEventRecurring] = useState(false);
  const [eventCancelTypeEnum, setEventCancelTypeEnum] = useState(
    CALENDAR_EVENT_CANCEL_TYPES[0].typeEnum
  );
  const cancelEventModalRef = useRef(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    reset,
  } = useForm({ criteriaMode: "all", mode: "onTouched" });

  const fetchAppointmentEventAPI = useAPI(
    SchedulingService.fetchAppointmentEvent,
    fetchAppointmentEventHandler,
    false
  );

  const updateEventAPI = useAPI(
    SchedulingService.updateEvent,
    updateEventHandler,
    false
  );

  const cancelEventAPI = useAPI(
    SchedulingService.cancelEvent,
    cancelEventHandler,
    false
  );

  useEffect(() => {
    fetchAppointmentEvent();
  }, [hospitalId]);

  const fetchAppointmentEvent = useCallback(() => {
    // Fetch patients only after hospital has been set
    if (hospitalId !== "") {
      fetchAppointmentEventAPI.execute(
        hospitalId,
        appointmentId,
        calendarEventId
      );
    }
  }, [fetchAppointmentEventAPI]);

  function updateEvent(data) {
    data = data.calendarEvent;

    data.eventGuests = eventGuestList.map((guest) => guest.email);

    data.startsAt = DateUtils.formatDateTimeToTimestamp(
      data.startDate + " " + data.startTime
    );
    data.endsAt = DateUtils.formatDateTimeToTimestamp(
      data.endDate + " " + data.endTime
    );

    updateEventAPI.execute(hospitalId, calendarEventId, data);
  }

  const cancelEvent = (cancelTypeEnum) => {
    cancelEventAPI.execute(hospitalId, calendarEventId, cancelTypeEnum);
  };

  /* API Success Handlers */

  function fetchAppointmentEventHandler(response) {
    let data = response.data;

    data.calendarEvent.startDate = DateUtils.formatUtcDateTimeToDateInput(
      data.calendarEvent.startsAt
    );
    data.calendarEvent.endDate = DateUtils.formatUtcDateTimeToDateInput(
      data.calendarEvent.endsAt
    );

    data.calendarEvent.startTime = DateUtils.formatUtcDateTimeToTimeInput(
      data.calendarEvent.startsAt
    );
    data.calendarEvent.endTime = DateUtils.formatUtcDateTimeToTimeInput(
      data.calendarEvent.endsAt
    );

    data.patientId = `${data.patientName} (${data.patientId})`;
    data.doctorId = `${data.doctorName} (${data.doctorId})`;
    reset(data);

    setIsEventRecurring(data.calendarEvent.isRecurring);

    const eventGuests = data.calendarEvent.eventGuests.map((guest) => {
      return {
        type: "guest",
        email: guest.email,
        isDisabled: true,
      };
    });

    setEventGuestList(eventGuests);
  }

  function updateEventHandler(response) {
    onCloseHandler();
  }

  function cancelEventHandler(response) {
    onCloseHandler();
  }

  return (
    <>
      <div className="my-2">
        <div
          className="d-flex justify-content-between align-items-center rounded p-3"
          style={{
            backgroundColor: "#eef3f8",
          }}
        >
          <Typography variant="h3">Update Appointment</Typography>
          {getValues("calendarEvent.statusType") ===
          "CANCELED" ? null : isEventRecurring ? (
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className="btn btn-outline-danger dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Cancel
              </button>
              <ul className="dropdown-menu">
                {CALENDAR_EVENT_CANCEL_TYPES.map((type, index) => {
                  return (
                    <li
                      key={`cancel-type-${index}`}
                      onClick={() => {
                        setEventCancelTypeEnum(type.typeEnum);
                        cancelEventModalRef.current.openModal();
                      }}
                    >
                      <span className="dropdown-item">{type.name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <Button
              type="button"
              size="small"
              color="error"
              variant="outlined"
              onClick={() => {
                cancelEventModalRef.current.openModal();
              }}
            >
              Cancel
            </Button>
          )}
        </div>
        <form className="m-3" onSubmit={handleSubmit(updateEvent)}>
          <div className="row">
            <div className="col col-6 my-3">
              <label htmlFor="patientInput" className="form-label">
                Patient
              </label>
              <input
                type="text"
                className="form-control"
                id="patientInput"
                disabled
                {...register("patientId", {
                  required: "Patient is required",
                })}
              />
            </div>
            <div className="col col-6 my-3">
              <label htmlFor="doctorInput" className="form-label">
                Doctor
              </label>
              <input
                type="text"
                className="form-control"
                id="doctorInput"
                disabled
                {...register("doctorId", {
                  required: "Doctor is required",
                })}
              />
            </div>
            <div className="col col-12 mb-3">
              <label htmlFor="titleInput" className="form-label">
                Title
              </label>
              <input
                type="text"
                className="form-control"
                id="titleInput"
                {...register("calendarEvent.title", {
                  required: "Title is required",
                })}
              />
              <InputErrorMessage name="calendarEvent.title" errors={errors} />
            </div>
            <div className="col col-12 mb-3">
              <label htmlFor="descriptionInput" className="form-label">
                Description
              </label>
              <textarea
                rows={3}
                className="form-control"
                id="descriptionInput"
                {...register("calendarEvent.description", {
                  required: "Description is required",
                })}
              />
              <InputErrorMessage
                name="calendarEvent.description"
                errors={errors}
              />
            </div>
            <div className="col col-6 mb-3">
              <label htmlFor="startDateInput" className="form-label">
                Start Date
              </label>
              <input
                type="date"
                className="form-control"
                id="startDateInput"
                {...register("calendarEvent.startDate", {
                  required: "Start date is required",
                })}
              />
              <InputErrorMessage
                name="calendarEvent.startDate"
                errors={errors}
              />
            </div>
            <div className="col col-6 mb-3">
              <label htmlFor="endDateInput" className="form-label">
                End Date
              </label>
              <input
                type="date"
                className="form-control"
                id="endDateInput"
                {...register("calendarEvent.endDate", {
                  required: "End date is required",
                })}
              />
              <InputErrorMessage
                name="calendarEvent.description"
                errors={errors}
              />
            </div>
            <div className="col col-3 mb-3">
              <label htmlFor="startTimeInput" className="form-label">
                Start Time
              </label>
              <input
                type="time"
                className="form-control"
                id="startTimeInput"
                {...register("calendarEvent.startTime", {
                  required: "Start time is required",
                })}
              />
              <InputErrorMessage
                name="calendarEvent.startTime"
                errors={errors}
              />
            </div>
            <div className="col col-3 mb-3">
              <label htmlFor="endTimeInput" className="form-label">
                End Time
              </label>
              <input
                type="time"
                className="form-control"
                id="endTimeInput"
                {...register("calendarEvent.endTime", {
                  required: "End time is required",
                })}
              />
              <InputErrorMessage name="calendarEvent.endTime" errors={errors} />
            </div>
            <div className="col col-6 mb-3">
              <label className="form-label d-block">Location Type</label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="locationTypeOnline"
                  name="locationType"
                  value="Online"
                  {...register("calendarEvent.locationType", {
                    required: "Location type is required",
                  })}
                />
                <label
                  className="form-check-label"
                  htmlFor="locationTypeOnline"
                >
                  Online
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="locationTypeOffline"
                  name="locationType"
                  value="Offline"
                  {...register("calendarEvent.locationType", {
                    required: "Location type is required",
                  })}
                />
                <label
                  className="form-check-label"
                  htmlFor="locationTypeOffline"
                >
                  Offline
                </label>
              </div>
              <InputErrorMessage
                name="calendarEvent.locationType"
                errors={errors}
              />
            </div>
            <div className="col col-7 mb-3">
              <label htmlFor="eventGuestInput" className="form-label">
                Event Guests
              </label>
              <div className="d-flex flex-row">
                <input
                  type="text"
                  className="form-control"
                  id="eventGuestInput"
                  placeholder="Guest email id"
                  value={eventGuest}
                  onChange={(event) => {
                    setEventGuest(event.target.value);
                  }}
                />
                <Button
                  type="button"
                  className="ms-2"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (eventGuest != "") {
                      setEventGuestList((prevState) => {
                        return [
                          ...prevState,
                          {
                            type: "guest",
                            email: eventGuest,
                            isDisabled: false,
                          },
                        ];
                      });
                      setEventGuest("");
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
            <div className="d-flex flex-wrap mb-3">
              {eventGuestList.map((guest, index) => {
                if (guest.isDisabled) {
                  return (
                    <Chip
                      key={index}
                      label={guest.email}
                      variant="outlined"
                      className="me-2 mb-2"
                    />
                  );
                }
                return (
                  <Chip
                    key={index}
                    label={guest.email}
                    variant="outlined"
                    className="me-2 mb-2"
                    onDelete={() => {
                      setEventGuestList((prevState) => {
                        let index = prevState.findIndex(
                          (guestItem) => guestItem.email === guest.email
                        );
                        prevState.splice(index, 1);
                        return [...prevState];
                      });
                    }}
                  />
                );
              })}
            </div>
          </div>
          <div className="text-end">
            {" "}
            <Button onClick={() => onCloseHandler()} variant="outlined">
              Close
            </Button>
            {getValues("calendarEvent.statusType") ===
            "CANCELED" ? null : updateEventAPI.status === "pending" ? (
              <LoadingButton
                loading
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="contained"
                className="ms-2"
              >
                Update
              </LoadingButton>
            ) : (
              <Button
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                className="ms-2"
              >
                Update
              </Button>
            )}
          </div>
        </form>
      </div>
      <PopUpModal ref={cancelEventModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm canceling the appointment?"
          confirmActionHandler={() => {
            cancelEvent(eventCancelTypeEnum);
            cancelEventModalRef.current.closeModal();
            onCloseHandler();
          }}
          onCloseHandler={() => cancelEventModalRef.current.closeModal()}
        />
      </PopUpModal>
    </>
  );
};

export default UpdateAppointmentModal;
